// client/src/modules/ai-systems/routes.tsx
import React from 'react';
import { Navigate } from 'react-router-dom';
import type { RouteObject } from 'react-router-dom';
import { ProtectedRoute } from '../../components/ProtectedRoute';
import { lazyWithRetry } from '../../lib/lazyWithRetry';

// Lazy load all AI Systems pages for code splitting
const Dashboard = lazyWithRetry(() => import('../../pages/AISystems/Dashboard'));
const PromptList = lazyWithRetry(() => import('../../pages/AISystems/PromptList'));
const PromptDetail = lazyWithRetry(() => import('../../pages/AISystems/PromptDetail'));
const PromptForm = lazyWithRetry(() => import('../../pages/AISystems/PromptForm'));
const PromptComparison = lazyWithRetry(() => import('../../pages/AISystems/PromptComparison'));
const OutputList = lazyWithRetry(() => import('../../pages/AISystems/OutputList'));
const CampaignList = lazyWithRetry(() => import('../../pages/AISystems/CampaignList'));
const CampaignDetail = lazyWithRetry(() => import('../../pages/AISystems/CampaignDetail'));
const CampaignForm = lazyWithRetry(() => import('../../pages/AISystems/CampaignForm'));
const TaskInbox = lazyWithRetry(() => import('../../pages/AISystems/TaskInbox'));
const SampleReview = lazyWithRetry(() => import('../../pages/AISystems/SampleReview'));

// Deployment pages (Phase 6)
const DeploymentList = lazyWithRetry(() =>
  import('../../pages/AISystems/Deployments/DeploymentList')
);
const DeploymentDetail = lazyWithRetry(() =>
  import('../../pages/AISystems/Deployments/DeploymentDetail')
);

// Experiments Dashboard (A/B tests, learning configs - renamed from "Learning")
const LearningDashboard = lazyWithRetry(() =>
  import('../../pages/AISystems/LearningDashboard')
);

// Discovery pages (AI Discovery capability)
const DiscoveryDashboard = lazyWithRetry(() =>
  import('../../pages/AISystems/Discovery/DiscoveryDashboard')
);
const DiscoverySessionList = lazyWithRetry(() =>
  import('../../pages/AISystems/Discovery/DiscoverySessionList')
);
const DiscoverySessionDetail = lazyWithRetry(() =>
  import('../../pages/AISystems/Discovery/DiscoverySessionDetail')
);
const DiscoverySessionWizard = lazyWithRetry(() =>
  import('../../pages/AISystems/Discovery/DiscoverySessionWizard')
);
const LandscapeReportViewer = lazyWithRetry(() =>
  import('../../pages/AISystems/Discovery/LandscapeReportViewer')
);

// Agent Framework pages
const AgentTaskInbox = lazyWithRetry(() =>
  import('../../pages/AISystems/Agents/AgentTaskInbox')
);
const AgentActivityFeed = lazyWithRetry(() =>
  import('../../pages/AISystems/Agents/AgentActivityFeed')
);
const AgentRunPage = lazyWithRetry(() =>
  import('../../pages/AISystems/Agents/AgentRunPage')
);

// Skills Library pages
const SkillsList = lazyWithRetry(() => import('../../pages/AISystems/Skills/SkillsList'));
const SkillDetail = lazyWithRetry(() => import('../../pages/AISystems/Skills/SkillDetail'));

// Mailroom pages
const MailroomDashboard = lazyWithRetry(() =>
  import('../../pages/AISystems/Mailroom/MailroomDashboard')
);
const EmailDetail = lazyWithRetry(() =>
  import('../../pages/AISystems/Mailroom/EmailDetail')
);
const ProcessorSettings = lazyWithRetry(() =>
  import('../../pages/AISystems/Mailroom/ProcessorSettings')
);
const ProcessorTraining = lazyWithRetry(() =>
  import('../../pages/AISystems/Mailroom/ProcessorTraining')
);

// Usage Dashboard (per-module AI metrics)
const UsageDashboard = lazyWithRetry(() => import('../../pages/AISystems/UsageDashboard'));

// Catch-Up Queue Dashboard
const CatchupDashboard = lazyWithRetry(() =>
  import('../../pages/AISystems/CatchupDashboard')
);

// Agent Admin pages (config audit trail + health dashboard)
const ConfigAuditTrail = lazyWithRetry(() =>
  import('../../pages/AISystems/ConfigAuditTrail')
);
const AgentHealthDashboard = lazyWithRetry(() =>
  import('../../pages/AISystems/AgentHealthDashboard')
);
const AgentWorkLog = lazyWithRetry(() =>
  import('../../pages/AISystems/AgentWorkLog')
);

// Train fin-ai — interview-driven training loop for the finance agent
const TrainFinAi = lazyWithRetry(() => import('../../pages/AISystems/TrainFinAi'));

/**
 * AI Systems routes - relative to /ai-systems prefix
 *
 * Organized around jobs-to-be-done:
 * - Dashboard: Overview of AI health + action items
 * - Monitor: Outputs, Discovery
 * - Improve: Prompts, Campaigns, Experiments
 * - Skills: Library of custom Al skills
 * - Automations: Agents (Inbox + Activity), Mailroom (Queue + Processors)
 * - Ship: Deployments
 */
export const routes: RouteObject[] = [
  // Dashboard (overview + action items)
  {
    index: true,
    element: <ProtectedRoute><Dashboard /></ProtectedRoute>,
  },

  // === MONITOR ===

  // Outputs
  {
    path: 'outputs',
    element: <ProtectedRoute><OutputList /></ProtectedRoute>,
  },

  // Usage (per-module AI metrics)
  {
    path: 'usage',
    element: <ProtectedRoute><UsageDashboard /></ProtectedRoute>,
  },

  // Discovery (flattened - dashboard is landing page)
  {
    path: 'discovery',
    element: <ProtectedRoute><DiscoveryDashboard /></ProtectedRoute>,
  },
  {
    path: 'discovery/sessions',
    element: <ProtectedRoute><DiscoverySessionList /></ProtectedRoute>,
  },
  {
    path: 'discovery/sessions/:sessionId',
    element: <ProtectedRoute><DiscoverySessionDetail /></ProtectedRoute>,
  },
  {
    path: 'discovery/new',
    element: <ProtectedRoute><DiscoverySessionWizard /></ProtectedRoute>,
  },
  {
    path: 'discovery/reports/:reportId',
    element: <ProtectedRoute><LandscapeReportViewer /></ProtectedRoute>,
  },

  // === IMPROVE ===

  // Prompts
  {
    path: 'prompts',
    element: <ProtectedRoute><PromptList /></ProtectedRoute>,
  },
  {
    path: 'prompts/new',
    element: <ProtectedRoute><PromptForm /></ProtectedRoute>,
  },
  {
    path: 'prompts/compare',
    element: <ProtectedRoute><PromptComparison /></ProtectedRoute>,
  },
  {
    path: 'prompts/:promptId',
    element: <ProtectedRoute><PromptDetail /></ProtectedRoute>,
  },
  {
    path: 'prompts/:promptId/edit',
    element: <ProtectedRoute><PromptForm /></ProtectedRoute>,
  },

  // Campaigns
  {
    path: 'campaigns',
    element: <ProtectedRoute><CampaignList /></ProtectedRoute>,
  },
  {
    path: 'campaigns/new',
    element: <ProtectedRoute><CampaignForm /></ProtectedRoute>,
  },
  {
    path: 'campaigns/:campaignId',
    element: <ProtectedRoute><CampaignDetail /></ProtectedRoute>,
  },
  {
    path: 'campaigns/:campaignId/edit',
    element: <ProtectedRoute><CampaignForm /></ProtectedRoute>,
  },
  {
    path: 'campaigns/:campaignId/review',
    element: <ProtectedRoute><SampleReview /></ProtectedRoute>,
  },

  // Experiments (A/B tests, learning configs - renamed from "Learning")
  {
    path: 'experiments',
    element: <ProtectedRoute><LearningDashboard /></ProtectedRoute>,
  },

  // === SHIP ===

  // Deployments
  {
    path: 'deployments',
    element: <ProtectedRoute><DeploymentList /></ProtectedRoute>,
  },
  {
    path: 'deployments/:deploymentId',
    element: <ProtectedRoute><DeploymentDetail /></ProtectedRoute>,
  },

  // === TASKS (accessible from Dashboard action items) ===

  // Tasks (still accessible, linked from Dashboard)
  {
    path: 'tasks',
    element: <ProtectedRoute><TaskInbox /></ProtectedRoute>,
  },
  {
    path: 'tasks/:taskId',
    element: <ProtectedRoute><SampleReview /></ProtectedRoute>,
  },

  // === SKILLS ===

  {
    path: 'skills',
    element: <ProtectedRoute><SkillsList /></ProtectedRoute>,
  },
  {
    path: 'skills/:skillId',
    element: <ProtectedRoute><SkillDetail /></ProtectedRoute>,
  },

  // === CATCH-UP ===

  // Catch-Up Queue — structured review of agent work product
  {
    path: 'catchup',
    element: <ProtectedRoute><CatchupDashboard /></ProtectedRoute>,
  },

  // === AGENTS ===

  // Agent Task Inbox — human-in-the-loop approval queue
  {
    path: 'agents',
    element: <ProtectedRoute><AgentTaskInbox /></ProtectedRoute>,
  },
  // Agent Activity Feed — timeline of agent runs
  {
    path: 'agents/activity',
    element: <ProtectedRoute><AgentActivityFeed /></ProtectedRoute>,
  },
  // One agent run by id - target of ModuleAgentFeed "Details", AgentAttribution "View full trace"
  // and Work Log "View full run" (without it they fell through to the catch-all below)
  {
    path: 'agents/activity/:runId',
    element: <ProtectedRoute><AgentRunPage /></ProtectedRoute>,
  },
  // Agent Config Audit Trail — version history + rollback
  {
    path: 'config-audit',
    element: <ProtectedRoute><ConfigAuditTrail /></ProtectedRoute>,
  },
  // Agent Health Dashboard — run metrics + cost tracking
  {
    path: 'agent-health',
    element: <ProtectedRoute><AgentHealthDashboard /></ProtectedRoute>,
  },
  // Agent Work Log — detailed per-run action log
  {
    path: 'work-log',
    element: <ProtectedRoute><AgentWorkLog /></ProtectedRoute>,
  },
  // Train fin-ai — knowledge interview loop. Admin-only to match the backend
  // requireOrgAdmin gate on every finance_* training callable (a Manager would
  // otherwise load the page then hit permission-denied on the first action).
  {
    path: 'train-fin-ai',
    element: (
      <ProtectedRoute requireAdmin>
        <TrainFinAi />
      </ProtectedRoute>
    ),
  },

  // === MAILROOM ===

  {
    path: 'mailroom',
    element: <ProtectedRoute requiredRole={['Owner']}><MailroomDashboard /></ProtectedRoute>,
  },
  {
    path: 'mailroom/processors',
    element: <ProtectedRoute><ProcessorSettings /></ProtectedRoute>,
  },
  {
    path: 'mailroom/:emailId',
    element: <ProtectedRoute requiredRole={['Owner']}><EmailDetail /></ProtectedRoute>,
  },
  {
    path: 'mailroom/:emailId/train',
    element: <ProtectedRoute requiredRole={['Owner']}><ProcessorTraining /></ProtectedRoute>,
  },

  // Redirects for old paths
  {
    path: 'learning',
    element: <Navigate to="/ai-systems/experiments" replace />,
  },

  // Catch all - redirect to dashboard
  {
    path: '*',
    element: <Navigate to="/ai-systems" replace />,
  },
];
